import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Card, CardContent, Skeleton, Typography } from '@mui/material';
import SubscribeSaveExplanation from '@components/SubscribeSaveExplanation';
import config from '@configFile';
import FeedSidebar from '@pages/Blog/components/FeedSidebar';
import useIntersectionObserver from '@hooks/useIntersectionObserver';
import Signin from '@components/Signin';
import TopDealsCard from '@components/TopDealsCard';

interface SidebarProps {
  shouldRenderTopPadding?: boolean;
  slug?: string;
}

const Sidebar: React.FC<SidebarProps> = React.memo(
  ({ shouldRenderTopPadding = true, slug = '' }) => {
    const ref = useRef<HTMLDivElement>(null);
    const [isPending, setisPending] = useState(false);
    const [limit, setLimit] = useState(4);
    const isVisible = useIntersectionObserver(ref);
    const isSubscribeAndSavePage = slug === 'best-subscribe-save-deals-amazon';

    const components = [
      isSubscribeAndSavePage && (
        <SubscribeSaveExplanation
          shouldRenderTopSpace={shouldRenderTopPadding}
          key="ss"
        />
      ),
      <Box
        key="td"
        sx={{
          mt: isSubscribeAndSavePage ? 0 : 2
        }}
      >
        <TopDealsCard tag={config.AFFILIATE_TAGS.JD_TOP_DEALS_DAY} />
      </Box>,
      <Box
        key="signin"
        sx={{
          padding: '0px 0rem 0px 1rem'
        }}
      >
        <Signin showInCard showDontHaveAccount />
      </Box>,
      <FeedSidebar key="tl" />
    ].filter(Boolean);

    const loadMore = useCallback(() => {
      setisPending(true);
      setTimeout(() => {
        setLimit((prevLimit) => prevLimit + 1);
        setisPending(false);
      }, 750);
    }, []);

    const hasMore = limit < components.length;

    useEffect(() => {
      if (isVisible && !isPending && hasMore) {
        loadMore();
      }
    }, [isVisible, isPending, hasMore, loadMore]);

    const componentsToRender = components.slice(0, limit);

    return (
      <Box
        sx={{
          position: 'relative',
          height: 'calc(100% - 32px)',
          display: { xs: 'none', md: 'block' }
        }}
      >
        {componentsToRender}
        {hasMore && (
          <Card
            sx={{
              margin: '1rem',
              padding: '1rem'
            }}
            ref={ref}
          >
            <Typography variant="h6" component="h2" gutterBottom>
              Loading...
            </Typography>
            <CardContent>
              <Skeleton
                variant="rectangular"
                width="100%"
                height={100}
                sx={{
                  marginBottom: '1rem'
                }}
              />
              <Skeleton
                variant="rectangular"
                width="70%"
                height={100}
                sx={{
                  marginBottom: '1rem'
                }}
              />
            </CardContent>
          </Card>
        )}
      </Box>
    );
  }
);

export default Sidebar;
